/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    array, func, object, string,
} from 'prop-types';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';

import { scrollToId } from '../helper';
import ProductDetails from './ProductDetails';
import ProductSummary from './ProductSummary';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import SubscriptionDialogContainer from './SubscriptionDialogContainer';

const useStyles = makeStyles({
    container: {
        paddingTop: '24px',
    },
    card: {
        background: '#FFFFFF',
        border: '1px solid #D9D9D9',
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        marginBottom: '50px',
        '@media screen and (max-width: 1024px)': {
            flexDirection: 'column',
        },
    },
});

const SubscriptionList = ({
    subscriptions,
    featureFlags,
    handleCancel,
    handleUpdate,
    customerId,
    brandColor,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [activeModal, setActiveModal] = useState('');
    const [selectedRuleId, setSelectedRuleId] = useState('');
    const gaGetButtonText = (buttonClicked) => (buttonClicked ? `${buttonClicked.substring(buttonClicked.indexOf('-') + 1)}` : '');

    const trackHandler = (eventActionValue, eventLabel) => {
        dispatch(trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: eventActionValue || 'click',
            eventLabel: eventLabel || 'view',
        }));
    };

    const handleModal = (modal, subscriptionRuleId, buttonClicked) => {
        trackHandler(`click - ${modal} Modal`, gaGetButtonText(buttonClicked));
        setActiveModal(modal);
        setSelectedRuleId(subscriptionRuleId);
        if (modal === '') {
            const id = `_Product_${subscriptionRuleId}`;
            setTimeout(() => scrollToId({ id }), 250);
        }
    };

    const handleUpdateSubscription = (event, id, buttonClicked) => {
        const eventLabel = gaGetButtonText(buttonClicked);
        switch (event) {
            case 'skip':
                trackHandler('click - Skip Modal', eventLabel);
                handleUpdate(event, id);
                setTimeout(() => handleModal('Skipped', selectedRuleId, buttonClicked), 500);
                break;

            case 'skipped':
                trackHandler('click - Skipped Modal', eventLabel);
                handleModal('', selectedRuleId);
                break;

            case 'pause':
                trackHandler('click - Pause Modal', eventLabel);
                handleUpdate(event, id);
                setActiveModal('');
                break;

            case 'resume':
                trackHandler('click - Resume Modal', eventLabel);
                handleUpdate(event, id);
                setActiveModal('');
                break;

            case 'cancel':
                trackHandler('click - Cancel Modal', eventLabel);
                handleCancel(id);
                setActiveModal('');
                break;

            case 'cancellation_survey':
                trackHandler('click - Cancellation Survey', eventLabel);
                break;

            case 'cancellation_survey_completed':
                trackHandler('Cancellation Survey Completed', eventLabel);
                handleCancel(id);
                setActiveModal('');
                break;

            default:
                break;
        }
        setTimeout(() => scrollToId({ id: `_Product_${selectedRuleId}` }), 250);
    };

    return (
        <div className={classes.container}>
            {subscriptions.map((product) => (
                <div className={classes.card} key={product.ruleId} id={`_Product_${product.ruleId}`}>
                    <ProductDetails data={product} featureFlags={featureFlags} />
                    <ProductSummary
                        data={product}
                        brandColor={brandColor}
                        customerId={customerId}
                        handleModal={handleModal}
                        featureFlags={featureFlags}
                        handleUpdateSubscription={handleUpdateSubscription}
                    />
                    {product.ruleId === selectedRuleId && (
                        <SubscriptionDialogContainer
                            activeModal={activeModal}
                            handleModal={handleModal}
                            featureFlags={featureFlags}
                            selectedRuleId={selectedRuleId}
                            subscription={product}
                            handleUpdateSubscription={handleUpdateSubscription}
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

SubscriptionList.propTypes = {
    subscriptions: array.isRequired,
    featureFlags: object.isRequired,
    handleCancel: func.isRequired,
    handleUpdate: func.isRequired,
    customerId: string.isRequired,
    brandColor: string.isRequired,
};

export default SubscriptionList;
